import { type ReactNode, useEffect, useRef } from "react";
import s from "./primitives.module.css";

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children?: ReactNode;
}

/** A modal that traps focus, closes on Escape and RESTORES FOCUS TO THE OPENER.
 *
 *  All three matter on this surface: the two dialogs that will exist are sign-in and withdraw, and a modal a
 *  keyboard user can tab out of — behind the scrim, into a page that is inert to the eye but not to the tab key
 *  — is one where the next Enter presses a control they cannot see.
 *
 *  It is the NATIVE `<dialog>` driven by `showModal()`, not a div with a hand-rolled trap. The platform gives
 *  the focus trap, the inert background, Escape and focus restoration by construction; a hand-rolled trap is
 *  ~40 lines of `querySelectorAll` that this app has no DOM test to verify, on the two screens where getting it
 *  wrong is a keyboard user pressing Enter on a control they cannot see. The two behaviours the platform does
 *  NOT give — closing on a backdrop click, and closing when React says `open` went false — are the two effects
 *  below, and they are the whole component. */
export function Dialog({ open, title, onClose, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // `showModal()` throws if the element is already open, and `close()` on a closed one is a no-op — so both
    // are guarded on the element's own state rather than on a React ref of what we think we did.
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // `close` covers every path OUT of the dialog: Escape, `close()`, and the form-method-dialog submit. One
    // handler means the parent's state cannot get out of step with the element's.
    const onNativeClose = () => onClose();
    el.addEventListener("close", onNativeClose);
    return () => el.removeEventListener("close", onNativeClose);
  }, [onClose]);

  return (
    // A click on the BACKDROP targets the dialog element itself (its children target themselves), which is the
    // only way to tell the two apart — a click inside must never close a dialog whose content is a withdrawal.
    <dialog
      ref={ref}
      className={s.dialog}
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <h2 className={s.dialogTitle}>{title}</h2>
      {children}
    </dialog>
  );
}
